import { useState, useEffect, useRef } from 'react';
import { useMicrophonePermission } from './useMicrophonePermission';

export function useAudioDetection() {
  const [isAudioActive, setIsAudioActive] = useState(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const analyzerRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const { hasPermission, requestPermission } = useMicrophonePermission();

  useEffect(() => {
    let mounted = true;

    const setupAudioContext = async () => {
      if (!hasPermission) {
        const granted = await requestPermission();
        if (!granted || !mounted) return;
      }

      try {
        streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
        
        if (!mounted) {
          if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
          }
          return;
        }

        audioContextRef.current = new AudioContext();
        analyzerRef.current = audioContextRef.current.createAnalyser();
        sourceRef.current = audioContextRef.current.createMediaStreamSource(streamRef.current);
        sourceRef.current.connect(analyzerRef.current);
        
        analyzerRef.current.fftSize = 2048;
      } catch (error) {
        console.error('Error setting up audio detection:', error);
      }
    };

    if (hasPermission) {
      setupAudioContext();
    }

    return () => {
      mounted = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (sourceRef.current) {
        sourceRef.current.disconnect();
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [hasPermission, requestPermission]);

  useEffect(() => {
    if (!analyzerRef.current || !hasPermission) return;

    const dataArray = new Uint8Array(analyzerRef.current.frequencyBinCount);
    let animationFrameId: number;

    const checkAudio = () => {
      if (!analyzerRef.current) return;
      
      analyzerRef.current.getByteFrequencyData(dataArray);
      const audioLevel = dataArray.reduce((acc, val) => acc + val, 0) / dataArray.length;
      setIsAudioActive(audioLevel > 30);
      animationFrameId = requestAnimationFrame(checkAudio);
    };

    checkAudio();

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [hasPermission]);

  return {
    isAudioActive,
    hasPermission
  };
}